// DTO de la pestaña FACTURACIÓN del directivo (rediseño 2026-07, mockup del
// responsable validado 2026-07-05).
//
//   - GET /api/directivo/facturacion?mes=YYYY-MM&q=1|2|3
//
// Resumen EJECUTIVO por corte (sin la operativa de conciliar, que sigue siendo
// del coordinador). Decisiones cerradas con el responsable:
//   - Navegación SOLO por cortes (Q1=1–15 · Q2=16–22 · Q3=23–fin).
//   - Proyección al cierre = facturado + pendiente (validado sin conciliar +
//     OPs abiertas + sin programar, a precio vigente) — no run-rate.
//   - Delta del año: vs corte ANTERIOR equivalente (no hay histórico 2025).
//   - Órdenes de tienda en la tabla con estado propio 'facturada_tienda'
//     (v1 sin cruce ni gate de validación — paridad pendiente).
// Los números salen de lib/facturacion.ts (las MISMAS funciones del
// coordinador) — no pueden divergir. Gate: puede_ver_costos (middleware).
import { z } from 'zod';
export const directivoFacturacionQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    /** Filtro opcional por planta (recalcula todo server-side). */
    planta_id: z.string().uuid().optional(),
})
    .refine((q) => q.desde <= q.hasta, { message: 'desde debe ser <= hasta' });
const nivelAggSchema = z.object({
    programadas: z.number(),
    realizadas: z.number(),
    validadas: z.number(),
    no_entregadas: z.number(),
    cumplimiento_pct: z.number().nullable(),
    cumplimiento_real_pct: z.number().nullable(),
});
export const facturacionOrdenSchema = z.object({
    tipo: z.enum(['op', 'tienda']),
    numero: z.string(),
    cliente_nombre: z.string(),
    area_nombre: z.string().nullable(),
    planta_nombre: z.string(),
    /** null en tiendas (sin programación aún — paridad pendiente). */
    programado: z.number().nullable(),
    /** OPs: validado; tiendas: unidades de la corrida. */
    unidades: z.number(),
    cumplimiento_pct: z.number().nullable(),
    /** OPs terminadas: valor facturado real; abiertas: ESTIMADO a precio vigente. */
    valor_cop: z.number(),
    estado: z.enum(['terminada', 'en_proceso', 'sin_programar', 'facturada_tienda']),
    cerrada_manual: z.boolean().nullable(),
    sin_precio: z.boolean(),
});
export const directivoFacturacionSchema = z.object({
    /** Eco del filtro de planta (null = todas). */
    planta_id: z.string().uuid().nullable(),
    /** Catálogo para el segmented de plantas del FE. */
    plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    corte: z.object({
        /** q_key SOLO cuando el rango ES exactamente un corte; null en día/semana/mes/rango. */
        q_key: z.string().nullable(),
        desde: z.string().date(),
        hasta: z.string().date(),
        dias_total: z.number().int(),
        /** Día del corte en curso (1..dias_total); solo aplica con q_key. */
        dia_actual: z.number().int().nullable(),
        en_curso: z.boolean(),
    }),
    /** Armado (OPs) facturado del corte — números del lib compartido. */
    facturado: z.object({
        valor_total: z.number(),
        valor_semanal: z.number(),
        valor_dominical: z.number(),
        unidades_validadas: z.number(),
        ops: z.number().int(),
        ops_con_recargo: z.number().int(),
        filas_sin_precio: z.number().int(),
    }),
    /** Tiendas facturadas en el corte (v1: sin cruce — paridad pendiente). */
    tiendas: z.object({
        unidades: z.number(),
        stickers: z.number(),
        termos: z.number(),
        valor_unidades: z.number(),
        ingreso_insumos: z.number(),
        valor_total: z.number(),
        filas_sin_precio: z.number().int(),
    }),
    /** CUADRO RESUMEN del corte por área. */
    areas: z.array(z.object({
        area_id: z.string().uuid(),
        area_codigo: z.string(),
        area_nombre: z.string(),
        ops: z.number().int(),
        unidades_validadas: z.number(),
        valor_total: z.number(),
    })),
    /** Pipeline vivo "por entrar al facturado" — estimados a precio vigente. */
    pendiente: z.object({
        sin_validar_cop: z.number(),
        ops_abiertas_cop: z.number(),
        sin_programar_cop: z.number(),
        total_cop: z.number(),
        ops_abiertas: z.number().int(),
        grupos_sin_programar: z.number().int(),
    }),
    /** Facturado (armado+tiendas) + pendiente total. */
    proyeccion_cierre_cop: z.number(),
    /** Puente producido → facturado sobre el RANGO del corte (estimados). */
    puente: z.object({
        producido_valido_cop: z.number(),
        sin_op_cop: z.number(),
        sin_programar_cop: z.number(),
        sin_validar_cop: z.number(),
        ops_abiertas_cop: z.number(),
        /** Producción de fechas previas al corte que facturó EN este corte (+). */
        conciliado_de_previos_cop: z.number(),
        /** Cierra la suma: recargo dominical + diferencias precio/ventana. */
        residuo_cop: z.number(),
        facturado_cop: z.number(),
        skus_sin_precio: z.number().int(),
    }),
    nivel_servicio: z.object({
        corte: nivelAggSchema,
        acumulado_anio: nivelAggSchema,
        /** Por día DEL RANGO seleccionado (vista diaria cuando el rango es corto). */
        dias: z.array(z.object({
            fecha: z.string().date(),
            programadas: z.number(),
            realizadas: z.number(),
            cumplimiento_pct: z.number().nullable(),
            cumplimiento_real_pct: z.number().nullable(),
        })),
        semanas: z.array(z.object({
            semana_iso: z.string(),
            programadas: z.number(),
            realizadas: z.number(),
            cumplimiento_pct: z.number().nullable(),
            cumplimiento_real_pct: z.number().nullable(),
        })),
        /** Para los chips de área del gráfico semanal. */
        semanas_area: z.array(z.object({
            semana_iso: z.string(),
            area_id: z.string().uuid(),
            area_codigo: z.string(),
            area_nombre: z.string(),
            programadas: z.number(),
            realizadas: z.number(),
            cumplimiento_pct: z.number().nullable(),
            cumplimiento_real_pct: z.number().nullable(),
        })),
    }),
    /** Órdenes del corte: terminadas (facturadas) + abiertas (estimado) + tiendas. */
    ordenes: z.array(facturacionOrdenSchema),
    /** Últimos cortes (incluye el actual) para la tendencia. */
    tendencia: z.array(z.object({
        q_key: z.string(),
        desde: z.string().date(),
        hasta: z.string().date(),
        valor_semanal: z.number(),
        valor_dominical: z.number(),
        valor_total: z.number(),
        en_curso: z.boolean(),
    })),
    anio: z.object({
        facturado_total_cop: z.number(),
        cortes_cerrados: z.number().int(),
        /** vs corte anterior equivalente; null si no hay corte anterior con valor. */
        delta_pct_vs_corte_anterior: z.number().nullable(),
        /** true → comparado contra el corte anterior TRUNCADO a la misma altura
         *  (corte en curso); false → contra el corte anterior completo. */
        delta_misma_altura: z.boolean(),
    }),
    actualizado_at: z.string(),
});
//# sourceMappingURL=directivo_facturacion.js.map